import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import App from './App';
import { AuthProvider } from './auth/AuthContext';
import { installChunkReloadGuard } from './chunkReloadGuard';
import { installClientErrorBeacon, reportClientError } from './clientErrorBeacon';
import CrashFallback from './components/CrashFallback';
import LoadingScreen from './components/LoadingScreen';
import NewVersionNotice from './components/NewVersionNotice';
import theme from './theme';
import { initSentry } from './sentry';
import {
  SCALEKIT_SILENT_CALLBACK_PATH,
  completeSilentCallback,
} from './auth/scalekit';
import { loadRuntimeAuthConfig } from './api/publicConfig';
import './styles.css';

const OnboardingWizardPage = lazy(() => import('./onboarding/OnboardingWizardPage'));
const McpDocsPage = lazy(() => import('./pages/McpDocsPage'));

/**
 * Some routes are fully public: they must render without booting Keycloak
 * (AuthProvider's mount effect calls initKeycloak, which enforces a secure
 * context and may redirect). These single-route shells never need the
 * authenticated router.
 *
 *   /signup    → self-service onboarding wizard (exits via full page loads).
 *   /docs/mcp  → the MCP connector docs page. PUBLIC by requirement: it is the
 *                Anthropic Connector Directory's Documentation URL, which must
 *                be reachable WITHOUT login. Keep it outside AuthProvider.
 *
 * The `dashboard-static` Cloudflare Worker serves any extensionless path by
 * falling back to index.html, so deep links to these routes boot the SPA here.
 */
const { pathname } = window.location;
const isPublicSignupRoute = pathname === '/signup';
const isPublicDocsRoute = pathname === '/docs/mcp';

/**
 * The Scalekit `prompt=none` renewal iframe (US-008). This document loads
 * INSIDE a hidden frame on the dashboard origin, and its entire job is to hand
 * the authorization response back to the parent frame. Booting React — let
 * alone `AuthProvider`, which would start its own session bootstrap — would
 * run a second, invisible copy of the dashboard on every token renewal.
 *
 * Handled before `initSentry()` too: the frame's URL carries the callback
 * `code`/`state`, and there is no UI here for a Sentry event to describe.
 */
const isSilentRenewalCallbackRoute = pathname === SCALEKIT_SILENT_CALLBACK_PATH;

const renderPublicRoute = (element: React.ReactNode) => (
  <BrowserRouter>
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="*" element={element} />
      </Routes>
    </Suspense>
  </BrowserRouter>
);

if (isSilentRenewalCallbackRoute) {
  // The frame needs the SAME client the parent used, and since US-011 that
  // comes from GET /api/config/public rather than from a build-time constant.
  // The frame has its own JS realm, so it has to fetch it for itself — one
  // cheap same-origin request against a client that has just proved it can
  // reach the backend. A failure falls back to the build-time flag exactly as
  // the parent does, which is the pre-US-011 behaviour.
  //
  // Deliberately ahead of the beacon, Sentry and the chunk guard: this frame
  // renders no app and loads no lazy chunk, so it needs none of them, and the
  // parent's silentRenewError path already owns reporting for this handshake.
  void loadRuntimeAuthConfig().then(completeSilentCallback);
} else {
  // First, ahead of initSentry(): the whole point of the beacon is to hold a
  // record when Sentry does not, which includes the case where Sentry's own
  // initialization is what failed.
  // Demo mode must not open a Sentry session or POST client errors. Both
  // calls fold back in when __DEMO__ is false.
  if (!__DEMO__) {
    installClientErrorBeacon();
    initSentry();
  }
  // Before any lazy chunk can fail: reload once when a dynamic import breaks
  // transiently instead of stranding the user on the ErrorBoundary (LUTIQ-74).
  installChunkReloadGuard();

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      {/* onError feeds the beacon directly: a render crash the boundary catches
          never reaches window.onerror in a production React build, so the window
          listener alone would miss exactly the failures that blank the page.
          reportClientError de-duplicates by error identity, so the development
          build's extra re-throw to window.onerror still counts as one. */}
      <Sentry.ErrorBoundary fallback={<CrashFallback />} onError={reportClientError}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {isPublicSignupRoute ? (
            renderPublicRoute(<OnboardingWizardPage />)
          ) : isPublicDocsRoute ? (
            renderPublicRoute(<McpDocsPage />)
          ) : (
            <AuthProvider>
              <App />
            </AuthProvider>
          )}
          {/* Outside the route branches on purpose: a tab left open on the
              signup wizard or the docs page goes stale the same way the
              dashboard does. */}
          <NewVersionNotice />
        </ThemeProvider>
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  );
}
